import React, { useState, useEffect } from 'react';
import { FileText, Users, ChevronRight, Loader, Download, X, User, Calendar, ClipboardList } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

/**
 * DireccionEncuestas - Ver respuestas de encuestas por estudiante
 */
const DireccionEncuestas = () => {
    const [encuestas, setEncuestas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEncuesta, setSelectedEncuesta] = useState(null);
    const [respuestas, setRespuestas] = useState([]);
    const [loadingRespuestas, setLoadingRespuestas] = useState(false);

    useEffect(() => {
        loadEncuestas();
    }, []);

    const loadEncuestas = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/direccion/encuestas`);
            if (response.ok) {
                const data = await response.json();
                setEncuestas(data.encuestas || []);
            }
        } catch (error) {
            console.error('Error loading encuestas:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadRespuestas = async (encuesta) => {
        setSelectedEncuesta(encuesta);
        setLoadingRespuestas(true);
        try {
            const response = await fetch(`${API_URL}/direccion/encuestas/${encuesta.id_encuesta}/respuestas`);
            if (response.ok) {
                const data = await response.json();
                setRespuestas(data.respuestas || []);
            }
        } catch (error) {
            console.error('Error loading respuestas:', error);
        } finally {
            setLoadingRespuestas(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('es-BO', {
            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader className="animate-spin mr-2" /> Cargando encuestas...
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Respuestas de Encuestas</h1>
                <p className="text-gray-500">Ver las respuestas de los estudiantes a las encuestas creadas</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Lista de encuestas */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                        <div className="bg-blue-600 px-4 py-3 text-white font-semibold flex items-center gap-2">
                            <ClipboardList size={20} />
                            Encuestas ({encuestas.length})
                        </div>
                        <div className="divide-y max-h-[500px] overflow-y-auto">
                            {encuestas.length === 0 ? (
                                <div className="p-8 text-center text-gray-500">
                                    No hay encuestas creadas
                                </div>
                            ) : (
                                encuestas.map(enc => (
                                    <div
                                        key={enc.id_encuesta}
                                        onClick={() => loadRespuestas(enc)}
                                        className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${selectedEncuesta?.id_encuesta === enc.id_encuesta ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                                            }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                                <p className="font-semibold text-gray-800">{enc.nombre}</p>
                                                <p className="text-sm text-gray-500">
                                                    {enc.objetivo?.substring(0, 50)}...
                                                </p>
                                                <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                                                    <span className={`px-2 py-0.5 rounded-full ${enc.estado === 'activa' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                                        }`}>
                                                        {enc.estado}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Users size={12} />
                                                        {enc.total_respuestas || 0} respuestas
                                                    </span>
                                                </div>
                                            </div>
                                            <ChevronRight size={20} className="text-gray-400" />
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Detalle de respuestas */}
                <div className="lg:col-span-2">
                    {!selectedEncuesta ? (
                        <div className="bg-gray-50 rounded-xl p-12 text-center">
                            <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                            <p className="text-gray-500">Selecciona una encuesta para ver las respuestas</p>
                        </div>
                    ) : loadingRespuestas ? (
                        <div className="flex items-center justify-center h-64">
                            <Loader className="animate-spin mr-2" /> Cargando respuestas...
                        </div>
                    ) : (
                        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                            <div className="bg-green-600 px-4 py-3 text-white">
                                <h3 className="font-bold">{selectedEncuesta.nombre}</h3>
                                <p className="text-sm opacity-90">{respuestas.length} respuestas recibidas</p>
                            </div>

                            {respuestas.length === 0 ? (
                                <div className="p-8 text-center text-gray-500">
                                    <Users size={32} className="mx-auto text-gray-300 mb-2" />
                                    No hay respuestas para esta encuesta
                                </div>
                            ) : (
                                <div className="divide-y max-h-[500px] overflow-y-auto">
                                    {respuestas.map((resp, idx) => (
                                        <div key={resp.id_respuesta || idx} className="p-4">
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                    <User size={20} className="text-blue-600" />
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-gray-800">
                                                        {resp.nombres || 'Estudiante'} {resp.apellidos || ''}
                                                    </p>
                                                    <p className="text-xs text-gray-500 flex items-center gap-2">
                                                        {resp.codigo_estudiante && <span>Código: {resp.codigo_estudiante}</span>}
                                                        <span className="flex items-center gap-1">
                                                            <Calendar size={10} />
                                                            {formatDate(resp.fecha_completado)}
                                                        </span>
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Mostrar respuestas del formulario */}
                                            <div className="bg-gray-50 rounded-lg p-3 text-sm">
                                                {resp.respuestas && typeof resp.respuestas === 'object' ? (
                                                    Object.entries(resp.respuestas).map(([pregunta, respuesta], i) => (
                                                        <div key={i} className="mb-2 last:mb-0">
                                                            <p className="text-gray-600 font-medium">
                                                                {pregunta.replace(/_/g, ' ')}:
                                                            </p>
                                                            <p className="text-gray-800 pl-2">
                                                                {typeof respuesta === 'object' ? JSON.stringify(respuesta) : respuesta}
                                                            </p>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <p className="text-gray-500 italic">Sin datos de respuestas</p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DireccionEncuestas;
